import React, { FC, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { ReactComponent as HomeIcon } from '../../../assets/icons/navigation/home.svg';
import t from '../../../i18n/t';
import {
  CALENDAR_SIDEBAR_TOGGLE_EASING,
  CALENDAR_SIDEBAR_TOGGLE_TRANSITION
} from '../../../utils/calendar/calendarConstants';
import { flyViewIcons } from '../../../utils/viewIconsFlight';
import { goToHomeLink, goToTasksLink } from '../navigationHelpers';
import NavigationItem from './NavigationItem';
import ViewSwitch, { COLUMN_SWITCH_HEIGHT } from './ViewSwitch';

type Props = {
  semesterId: number;
  sidebarCollapsed: boolean;
  calendarStartDate: any;
  resetNavigation: () => void;
};

// A tile in the column and the space above it (NavigationItem), and the same for the stacked switch.
const TILE_ROOM = 42;
const PAIR_ROOM = COLUMN_SWITCH_HEIGHT + 10;

/**
 * The top of the narrow icon column, which follows the left sidebar and never the page, so it only changes when the
 * student opens or closes the sidebar (Petr, 2026-11-01). With the sidebar open, Home, lit on the two pages its switch
 * moves between. With it closed the switch is out of sight, so a stacked version of it stands here instead, looking the
 * same (Petr, 2026-11-01), Home fading into it and the icons below gliding down to make room.
 */
const ColumnViews: FC<Props> = ({ semesterId, sidebarCollapsed, calendarStartDate, resetNavigation }) => {
  const { pathname } = useLocation();
  const pair = useRef<HTMLDivElement>(null);
  const wasCollapsed = useRef(sidebarCollapsed);

  useLayoutEffect(() => {
    if (wasCollapsed.current === sidebarCollapsed) return;
    wasCollapsed.current = sidebarCollapsed;
    if (pair.current) flyViewIcons(pair.current, sidebarCollapsed);
  }, [sidebarCollapsed]);

  const homeLink = goToHomeLink(semesterId);
  const tasksLink = goToTasksLink(semesterId);

  return (
    <Slot $height={sidebarCollapsed ? PAIR_ROOM : TILE_ROOM}>
      <Layer $shown={!sidebarCollapsed}>
        <NavigationItem
          tooltip={t.HOME_TAB_TEXT}
          link={homeLink}
          locationState={{ calendarStartDate: calendarStartDate?.toDate?.() || calendarStartDate }}
          onClick={() => resetNavigation()}
          Icon={HomeIcon}
          isActive={() => pathname === homeLink || pathname === tasksLink}
        />
      </Layer>
      <Layer ref={pair} $shown={sidebarCollapsed}>
        <ViewSwitch vertical />
      </Layer>
    </Slot>
  );
};

export default ColumnViews;

const Slot = styled.div<{ $height: number }>`
  position: relative;
  flex-shrink: 0;
  height: ${props => props.$height}px;
  transition: height ${CALENDAR_SIDEBAR_TOGGLE_TRANSITION}ms ${CALENDAR_SIDEBAR_TOGGLE_EASING};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Layer = styled.div<{ $shown: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  opacity: ${props => (props.$shown ? 1 : 0)};
  visibility: ${props => (props.$shown ? 'visible' : 'hidden')};
  ${props => !props.$shown && 'pointer-events: none;'}
  transition: opacity ${CALENDAR_SIDEBAR_TOGGLE_TRANSITION}ms ${CALENDAR_SIDEBAR_TOGGLE_EASING},
    visibility ${CALENDAR_SIDEBAR_TOGGLE_TRANSITION}ms;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;
